import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { EventName, EventSource } from '@loyalty/shared-common';
import { QlfRte } from '@qualifio/ui-commons/dist/components/qlf-rte';
import { RuleType } from '@loyalty/shared-common/rule-api-client';
import { useTranslation } from 'react-i18next';
import { BurningRuleFormValue } from '../../burning-rules/form';
import { EarningRuleFormValue } from '../../earning-rules/form';
import { LanguageTabs } from '../../shared/language-tabs';
import { onQlfRteChange } from '../../shared/forms/on-qlf-rte-change';
import { requiresReferral } from '../utils';
import { useProgram } from '../../loyalty/ProgramContext';
import { useRuleRTELabel } from '../rule-table-constants';

interface Props {
  className: string;
  type: RuleType;
  disabled?: boolean;
}

function isReferral(type: RuleType, eventSource: string, eventName: string): boolean {
  return (
    type === RuleType.EARN &&
    eventSource === EventSource.REFERRAL &&
    eventName === EventName.ECOMMERCE
  );
}

export function RulePanelDescription({ type, disabled }: Props): React.ReactElement {
  const { t } = useTranslation();
  const { program } = useProgram();
  const {
    control,
    formState: { errors },
    watch,
    getValues,
  } = useFormContext<EarningRuleFormValue | BurningRuleFormValue>();

  const eventSource = watch('eventSource');
  const eventName = watch('eventName');
  const requiresReferralCode = requiresReferral(watch('criteria'));
  const rteLabels = useRuleRTELabel();

  const introductionText = isReferral(type, eventSource, eventName)
    ? t('earning-rules.panels.description.message.referrer')
    : requiresReferralCode
    ? t('earning-rules.panels.description.message.referee')
    : t('earning-rules.panels.description.message');

  return (
    <LanguageTabs
      languages={program.languages}
      message={introductionText}
      errors={errors.i18nAttributes}
    >
      {({ language }) => (
        <div key={language}>
          <Controller
            control={control}
            name={`i18nAttributes.${language}.description`}
            shouldUnregister={false}
            render={({ field: { ref, ...field }, fieldState: { error } }) => {
              return (
                <QlfRte
                  disabled={disabled}
                  {...field}
                  onChange={onQlfRteChange(
                    field.onChange,
                    () => getValues().i18nAttributes?.[language]?.description,
                  )}
                  inputRef={ref}
                  error={!!error}
                  helperText={error?.message}
                  label={rteLabels[type]}
                  multiline
                />
              );
            }}
          />
        </div>
      )}
    </LanguageTabs>
  );
}
